import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { isNotFound, isPermissionDenied } from '@/common/error';

import { getOwnedVessels, getVesselById, getVesselsByIds } from './vessels.api';
import type {
  GetOwnedVesselsParams,
  GetVesselsByIdsParams,
  Vessel,
  VesselsPage,
} from './vessels.types';

const VESSELS = ['vessels'] as const;

export const vesselKeys = {
  all: VESSELS,
  /**
   * Both list shapes sit under this prefix, because the detail view seeds itself from
   * whichever of them the user arrived through.
   */
  ownedAll: [...VESSELS, 'owned'] as const,
  owned: (params: Partial<GetOwnedVesselsParams>) => [...vesselKeys.ownedAll, params] as const,
  byIds: (params: Partial<GetVesselsByIdsParams>) =>
    [...vesselKeys.ownedAll, 'by-ids', { ...params, ids: [...(params.ids ?? [])].sort() }] as const,
  detail: (id: string) => [...VESSELS, 'detail', id] as const,
};

/**
 * A vessel that is not the caller's is refused, not answered — as a 404 if the service
 * hides its existence, as a role rejection if it does not. Both are settled facts, and
 * both mean the same thing to this app: there is nothing here for you.
 */
export const isVesselUnavailable = (error: unknown) =>
  isNotFound(error) || isPermissionDenied(error);

export const useOwnedVesselsQuery = ({
  ownerId,
  serviceId,
  ...params
}: Partial<GetOwnedVesselsParams>) =>
  useQuery({
    queryKey: vesselKeys.owned({ ownerId, serviceId, ...params }),
    queryFn: () => getOwnedVessels({ ownerId: ownerId!, serviceId: serviceId!, ...params }),
    enabled: !!ownerId && !!serviceId,
  });

/**
 * The named vessels, in one request. What the account list is built from: the caller's role
 * rows say which vessels they may manage a plan for — owned or captained — and this fetches
 * exactly those.
 */
export const useVesselsByIdsQuery = ({ ids, serviceId }: Partial<GetVesselsByIdsParams>) =>
  useQuery({
    queryKey: vesselKeys.byIds({ ids, serviceId }),
    queryFn: () => getVesselsByIds({ ids: ids!, serviceId: serviceId! }),
    enabled: !!serviceId && !!ids?.length,
  });

/**
 * The vessel already sitting in a cached owned-vessels page, if one is. A list row carries
 * every field the detail view renders, so arriving by click has nothing left to wait for.
 */
const cachedListVessel = (queryClient: QueryClient, id: string): Vessel | undefined =>
  queryClient
    .getQueriesData<VesselsPage>({ queryKey: vesselKeys.ownedAll })
    .flatMap(([, page]) => page?.items ?? [])
    .find((vessel) => vessel.id === id);

/**
 * One vessel, addressed directly rather than found in a list — so a link to it costs a
 * single request and does not depend on it falling inside the first page of the owner's
 * vessels.
 *
 * Seeded from the list cache as placeholder data: a row click paints immediately and the
 * request only confirms what is shown. Placeholder rather than initial data, so the fetch
 * still happens and a name edited elsewhere is not pinned to whatever the list last saw.
 */
export const useVesselQuery = (id?: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: vesselKeys.detail(id!),
    queryFn: () => getVesselById({ id: id!, serviceId: id! }),
    enabled: !!id,
    placeholderData: () => (id ? cachedListVessel(queryClient, id) : undefined),
    // Asking again cannot turn a refusal into a vessel — retrying only delays the fallback
    // to the list.
    retry: (failureCount, error) => !isVesselUnavailable(error) && failureCount < 3,
  });
};
